import { getReferenceString, MedplumClient } from '@medplum/core';
import { PlanDefinition, Questionnaire, Reference, Task } from '@medplum/fhirtypes';

/**
 * Orders tasks according to the PlanDefinition action order by matching
 * Questionnaire url with action definitionCanonical.
 *
 * @param medplum - Medplum client
 * @param tasks - Array of tasks to order
 * @param planDefinition - PlanDefinition resource with action array
 * @returns Ordered array of tasks matching the action order, with unmatched tasks appended at the end
 */
export async function orderTasksByPlanDefinition(
  medplum: MedplumClient,
  tasks: Task[],
  planDefinition: PlanDefinition
): Promise<Task[]> {
  // If no actions defined, return tasks as-is
  if (!planDefinition.action || planDefinition.action.length === 0) {
    return tasks;
  }

  // Create a map of questionnaire URL to task for quick lookup
  const questionnaireUrlToTask = new Map<string, Task>();

  // First, read all questionnaires in parallel and build the mapping
  const questionnaireReadPromises = tasks
    .filter((task) => task.focus)
    .map(async (task) => {
      // Verify that focus is a Questionnaire reference before reading
      const focusRef = task.focus!;
      const referenceString = getReferenceString(focusRef);

      if (!referenceString?.startsWith('Questionnaire/')) {
        return { task, questionnaire: null };
      }

      try {
        const questionnaire = await medplum.readReference<Questionnaire>(focusRef as Reference<Questionnaire>);
        return { task, questionnaire };
      } catch (_error) {
        // If we can't read the questionnaire, the task will be included at the end as unmatched
        return { task, questionnaire: null };
      }
    });

  const results = await Promise.all(questionnaireReadPromises);

  for (const { task, questionnaire } of results) {
    if (questionnaire?.url) {
      questionnaireUrlToTask.set(questionnaire.url, task);
    }
  }

  // Order tasks according to action order
  const orderedTasks: Task[] = [];
  const matchedTaskIds = new Set<string>();

  for (const action of planDefinition.action) {
    if (!action.definitionCanonical) {
      continue;
    }

    const task = questionnaireUrlToTask.get(action.definitionCanonical);
    if (task && task.id) {
      orderedTasks.push(task);
      matchedTaskIds.add(task.id);
    }
  }

  // Add any tasks that weren't matched to any action
  for (const task of tasks) {
    if (task.id && !matchedTaskIds.has(task.id)) {
      orderedTasks.push(task);
    }
  }

  return orderedTasks;
}
